import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ImageTimer from './ImageTimer'; // <-- ADD THIS
// Firebase imports for editor mode
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Simple component stubs for now - we'll implement these properly later
/* FIX: Commented out unused component
const Header = () => (
  <div className="text-center mb-8">
    <h1 className="text-3xl font-bold text-white mb-2">Professional Headshot Generator</h1>
    <p className="text-gray-300">Create stunning professional headshots with AI-powered customization</p>
  </div>
);
*/

const ImageUploader = ({ title, onImageUpload, previewUrl, helpText, validationStatus = 'idle', validationMessage = '', detectedGender = null, footerText }) => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraError(null);
    } catch (err) {
      console.error("Camera access denied:", err);
      setCameraError("Camera access was denied. Please enable it in your browser settings.");
      setIsCameraOpen(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (isCameraOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [isCameraOpen, startCamera, stopCamera]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      onImageUpload(file);
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if(context){
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], 'selfie.png', { type: 'image/png' });
                onImageUpload(file);
                setIsCameraOpen(false);
            }
        }, 'image/png');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getValidationColor = (status) => {
    switch (status) {
      case 'valid': return 'bg-green-900/30 border border-green-700 text-green-300';
      case 'invalid': return 'bg-red-900/30 border border-red-700 text-red-300';
      case 'validating': return 'bg-yellow-900/30 border border-yellow-700 text-yellow-300';
      default: return 'bg-gray-800/30 border border-gray-700 text-gray-400';
    }
  };

  return (
    <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700">
       <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>

       {isCameraOpen ? (
        <div className="space-y-4">
            <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg bg-gray-900 aspect-square object-cover" />
            <div className="flex gap-4">
                <button onClick={handleCapture} className="flex-1 bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700">Capture</button>
                <button onClick={() => setIsCameraOpen(false)} className="flex-1 bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-500">Cancel</button>
            </div>
        </div>
       ) : (
        <>
            <div
                className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-[#FACC15] transition-colors relative"
                onClick={handleUploadClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleUploadClick()}
                aria-label={`Upload image for ${title}`}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp"
                />
                {previewUrl ? (
                <div className="relative group">
                    <img src={previewUrl} alt="Upload preview" className="mx-auto max-h-60 rounded-lg" />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                        Click to change image
                    </div>
                    {validationStatus !== 'idle' && (
                      <div className={`mt-2 text-xs font-semibold px-3 py-2 rounded-lg ${getValidationColor(validationStatus)}`}>
                        {validationStatus === 'validating' ? 'Validating...' : validationMessage}
                      </div>
                    )}
                </div>
                ) : (
                <div className="flex flex-col items-center justify-center space-y-2 text-gray-300">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="font-semibold">Click to upload file</p>
                    <p className="text-sm">or</p>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setIsCameraOpen(true); }} className="text-[#FACC15] font-semibold hover:underline">Use Camera</button>
                </div>
                )}
            </div>
            <div className="mt-4 text-xs text-gray-400 text-center">
              {footerText ? <p>{footerText}</p> : <p>Accepted: JPG, PNG, WebP. Max size: 20MB. Recommended: ≥1200px.</p>}
            </div>
            {cameraError && <p className="mt-2 text-sm text-red-600 text-center">{cameraError}</p>}
        </>
       )}

      {helpText && (
        <div className="mt-4 text-sm text-gray-400">
          <p><strong>Tip:</strong> {helpText}</p>
        </div>
      )}
    </div>
  );
};

const OptionSelector = ({ title, options, selectedOptionId, onSelectOption, isOptionAllowed, optionType }) => (
  <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700">
    <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {options.map(option => {
        const isAllowed = isOptionAllowed ? isOptionAllowed(option.id, optionType) : true;
        const isSelected = selectedOptionId === option.id;
        
        return (
          <button
            key={option.id}
            onClick={() => isAllowed && onSelectOption(option.id)}
            disabled={!isAllowed}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              isSelected
                ? 'border-[#FACC15] bg-yellow-900/20 shadow-md'
                : isAllowed
                ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                : 'border-gray-800 bg-gray-900/50 opacity-50 cursor-not-allowed'
            }`}
          >
            <h3 className="font-semibold text-white">{option.name}</h3>
            <p className="text-sm text-gray-300 mt-1">{option.description}</p>
            {!isAllowed && (
              <p className="text-xs text-red-400 mt-2">Restricted by team template</p>
            )}
          </button>
        );
      })}
    </div>
  </div>
);


const Preview = ({ isLoading, error, onRegenerate, onSaveToDrive, onSessionExpired, profile, generationAttempts, sessionGeneratedImages = [], imageExpirations = {}, hasSufficientCredits }) => { // <-- ADD onSessionExpired
  const [savingStates, setSavingStates] = useState({});
  const [currentTime, setCurrentTime] = useState(Date.now()); // <-- 1. ADD THIS STATE

  // 2. ADD THIS ENTIRE useEffect HOOK
  useEffect(() => {
    if (sessionGeneratedImages.length === 0) return; // Don't run timer if no images

    // This timer just forces this component to re-render to check expiration
    const timer = setInterval(() => {
      const now = Date.now();
      setCurrentTime(now);

      // --- 3. ADD STUCK STATE DETECTION ---
      if (generationAttempts >= 4 && sessionGeneratedImages.length > 0) {
        const allExpired = sessionGeneratedImages.every(img => { // <-- img is object
            const expTime = imageExpirations[img.id]; // <-- Check by img.id
            return !expTime || now > expTime;
        });

        if (allExpired) {
          console.log("Preview: Detected all images expired.");
          onSessionExpired();
          clearInterval(timer); // Stop this timer
        }
      }
      // --- END OF STUCK STATE DETECTION ---

    }, 1000); // Check every second

    return () => clearInterval(timer);
  }, [sessionGeneratedImages, generationAttempts, imageExpirations, onSessionExpired]); // <-- 4. ADD DEPENDENCIES

  // Format time for display

  const handleSaveClick = async (image, index) => { // <-- 1. Receive 'image' object
    setSavingStates(prev => ({ ...prev, [index]: true }));
    const success = await onSaveToDrive(image); // <-- 2. Pass 'image' object
    if (!success) {
      setSavingStates(prev => ({ ...prev, [index]: false }));
    }
    // On success, the parent component will reset the session, causing this component to re-render correctly.
  };


  const Placeholder = () => (
    <div className="flex flex-col items-center justify-center text-center p-8 bg-[#1a1a1a] rounded-xl border border-[#FACC15] h-full">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="mt-4 font-semibold text-white">Your new headshot will appear here</p>
      <p className="text-sm text-gray-300">Upload an image and make your selections to get started.</p>
    </div>
  );

  return (
    <div className="w-full">
      {isLoading && (
        <div className="text-center p-8 bg-[#1a1a1a] rounded-xl border border-[#FACC15] min-h-[400px] flex items-center justify-center">
          <div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FACC15] border-t-yellow-200 mx-auto"></div>
            <p className="mt-4 text-white font-medium">Generating your professional headshot...</p>
            <p className="text-sm text-gray-300">This may take a few moments</p>
          </div>
        </div>
      )}
      {!isLoading && error && (
        <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg relative mb-6" role="alert">
          <h3 className="font-bold">Generation Failed</h3>
          <p>{error}</p>
        </div>
      )}
      {!isLoading && !error && sessionGeneratedImages.length > 0 && (
        <div className="mb-8 p-6 border border-[#FACC15] bg-[#1a1a1a] rounded-xl">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-[#FACC15]">Current Session</h3>
              <div className="bg-[#FACC15] text-[#121212] px-3 py-1 rounded-full text-sm font-bold">
                {4 - generationAttempts} retakes left
              </div>
            </div>
            <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-yellow-400 font-bold">⚠️</span>
                <div>
                  <p className="text-yellow-100 font-medium">Download your favorite headshots to save them permanently</p>
                  <p className="text-yellow-200 text-xs mt-1">Each download uses 1 credit from your account</p>
                  <div className="mt-2 p-2 bg-yellow-800/30 rounded border border-yellow-500">
                      <p className="text-yellow-100 text-xs font-medium">⏰ Time-Sensitive Download</p>
                      <p className="text-yellow-200 text-xs mt-1">
                          Your generated images will be automatically deleted in: <span className="font-bold text-yellow-300">15:00</span>
                      </p>
                      <p className="text-yellow-200 text-xs mt-1">
                          For privacy protection, we don't store your images permanently. Please download within 15 minutes.
                      </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-2 gap-2">
            {sessionGeneratedImages.map((img, index) => ( // <-- img is object
              <div key={img.id} className="relative group break-inside-avoid mb-2"> {/* <-- 1. Use img.id as key */}
                <img
                  src={img.url} // <-- 2. Use img.url
                  alt={`Session headshot ${index + 1}`}
                  className="w-full rounded-lg object-contain cursor-default pointer-events-none"
                />
                {/* Individual timer for each image */}
                {imageExpirations[img.id] && ( // <-- 3. Check by img.id
                  <ImageTimer expirationTime={imageExpirations[img.id]} /> // <-- 4. Pass by img.id
                )}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <button
                    onClick={() => handleSaveClick(img, index)} // <-- 5. Pass 'img' object
                    // ADD THE EXPIRATION CHECK HERE
                    disabled={
                      savingStates[index] ||
                      !hasSufficientCredits() ||
                      !imageExpirations[img.id] || // <-- 6. Check by img.id
                      currentTime > imageExpirations[img.id] // <-- 7. Check by img.id
                    }
                    className="bg-black bg-opacity-70 px-4 py-2 rounded-lg border-2 border-[#FACC15] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="bg-[#FACC15] text-[#121212] px-3 py-1 rounded-lg font-bold text-sm">
                      {savingStates[index] ? 'Saving...' : 'Download (1 Credit)'}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            <button
              onClick={onRegenerate}
              disabled={!hasSufficientCredits() || generationAttempts >= 4}
              className="w-full bg-gradient-to-r from-[#FACC15] to-yellow-400 hover:from-[#FACC15] hover:to-yellow-300 text-[#121212] font-bold py-3 px-4 rounded-lg text-lg transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center"
            >
              Regenerate ({4 - generationAttempts} retakes left)
            </button>
            <p className="text-xs text-gray-400 text-center">
              <strong>Generate:</strong> Creates new headshots • <strong>Regenerate:</strong> Creates variation of last generated image of current session
            </p>
            {generationAttempts >= 4 && <p className="text-center text-sm text-red-400 mt-2">Save an image to start a new session.</p>}
          </div>
        </div>
      )}
      {!isLoading && !error && sessionGeneratedImages.length === 0 && <Placeholder />}
    </div>
  );
};

// NEW: Debug component to show the final payload
// const DebugPreview = ({ prompt, images, isTwoStep, step1Prompt, step2Prompt }) => {
//   if (!prompt || images.length === 0) {
//     return null; // Don't render if there's no data yet
//   }
//
//   return (
//     <div className="bg-yellow-900/20 p-4 rounded-lg shadow-md border border-yellow-600 mt-4">
//       <h3 className="text-lg font-bold text-[#FACC15] mb-2">DEBUG: Generation Payload</h3>
//       <div className="space-y-4">
//         {isTwoStep ? (
//           <>
//             <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
//               <h4 className="font-semibold text-yellow-700 mb-2">⚠️ Two-Step Generation Process</h4>
//               <div className="space-y-3">
//                 <div>
//                   <h5 className="font-medium text-yellow-600">Step 1 Prompt:</h5>
//                   <p className="text-xs text-gray-300 bg-[#1a1a1a] p-2 rounded border border-yellow-600 font-mono break-words">{step1Prompt}</p>
//                 </div>
//                 <div>
//                   <h5 className="font-medium text-yellow-600">Step 2 Prompt:</h5>
//                   <p className="text-xs text-gray-300 bg-[#1a1a1a] p-2 rounded border border-yellow-600 font-mono break-words">{step2Prompt}</p>

//                 </div>
//               </div>
//             </div>
//             <div>
//               <h4 className="font-semibold text-[#FACC15]">Original Prompt:</h4>
//               <p className="text-xs text-gray-300 bg-[#1a1a1a] p-2 rounded border border-yellow-600 font-mono break-words">{prompt}</p>
//             </div>
//           </>
//         ) : (
//           <div>
//             <h4 className="font-semibold text-[#FACC15]">Final Prompt:</h4>
//             <p className="text-xs text-gray-300 bg-[#1a1a1a] p-2 rounded border border-yellow-600 font-mono break-words">{prompt}</p>
//           </div>
//         )}
//         <div>
//           <h4 className="font-semibold text-[#FACC15]">Images Sent (in order):</h4>
//           <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
//             {images.map((image, index) => (
//               <div key={index} className="text-center p-1 bg-[#1a1a1a] rounded border border-yellow-600">
//                 <img src={image.previewUrl} alt={`Input ${index + 1}: ${image.role}`} className="mx-auto max-h-24 w-auto rounded" />
//                 <p className="text-xs font-semibold mt-1">{`[${index + 1}] ${image.role}`}</p>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

const CollapsibleSection = ({ title, children }) => (
  <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700">
    <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
    {children}
  </div>
);

/* FIX: Commented out unused component
const Slider = ({ label, value, min, max, onChange }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-white mb-2">
      {label}: <span className="font-bold">{value}</span>
    </label>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
      style={{
        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((value - min) / (max - min)) * 100}%, #e5e7eb ${((value - min) / (max - min)) * 100}%, #e5e7eb 100%)`
      }}
    />
  </div>
);
*/

/* FIX: Commented out unused component
const Toggle = ({ label, enabled, onChange }) => (
  <label className="flex items-center mb-4 cursor-pointer">
    <div className="relative">
      <input
        type="checkbox"
        checked={enabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div className={`w-10 h-6 rounded-full transition-colors ${
        enabled ? 'bg-[#FACC15]' : 'bg-gray-600'
      }`}></div>
      <div className={`absolute left-1 top-1 bg-gray-300 w-4 h-4 rounded-full transition-transform ${
        enabled ? 'transform translate-x-4' : ''
      }`}></div>
    </div>
    <span className="ml-3 text-sm font-medium text-white">{label}</span>
  </label>
);
*/


// Enhanced constants with complete options from Cloud Run interface
const CATEGORIES = [
  { id: 'corporate', name: 'Corporate', description: 'Classic and modern professional looks for business environments.' },
  { id: 'medical', name: 'Medical / Healthcare', description: 'Medical professional attire including scrubs and lab coats.' },
  { id: 'academic', name: 'Academic', description: 'For educators, researchers, and academic professionals.' }
];

const CORPORATE_WARDROBE_OPTIONS = [
  { id: 'business_formal', name: 'Business Formal', attire: { male: 'a classic business suit with tie', female: 'a professional business suit or blazer' } },
  { id: 'business_casual', name: 'Business Casual', attire: { male: 'a stylish sweater or collared shirt', female: 'a stylish blouse or sweater' } },
  { id: 'tech_casual', name: 'Tech Casual', attire: { male: 'a modern tech company polo or casual button-down', female: 'a modern tech company blouse or casual top' } },
  { id: 'executive', name: 'Executive', attire: { male: 'an executive-level suit with premium accessories', female: 'an executive-level suit or premium business attire' } }
];

const CORPORATE_BACKGROUND_OPTIONS = [
  { id: 'blurred_office', name: 'Blurred Office', backgroundAndLighting: 'Professional office background with soft focus' },
  { id: 'studio_gray', name: 'Studio Gray', backgroundAndLighting: 'Clean studio background with professional lighting' },
  { id: 'modern_office', name: 'Modern Office', backgroundAndLighting: 'Contemporary office environment with natural light' },
  { id: 'glass_wall', name: 'Glass Wall', backgroundAndLighting: 'Modern glass-walled conference room' },
  { id: 'library', name: 'Library', backgroundAndLighting: 'Professional library or bookshelf background' },
  { id: 'white', name: 'White', backgroundAndLighting: 'Clean white background with professional lighting' }
];

const MEDICAL_PRESETS = [
  {
    id: 'medical_scrubs',
    name: 'Medical Scrubs',
    promptFragments: { male: 'medical scrubs', female: 'medical scrubs' },
    supportsColorOverride: true,
    defaultColor: '#2E8B57' // Sea Green
  },
  {
    id: 'medical_coat',
    name: 'Medical Coat',
    promptFragments: { male: 'white medical lab coat', female: 'white medical lab coat' },
    supportsColorOverride: false
  }
];

const MEDICAL_BACKGROUND_PRESETS = [
  {
    id: 'clinic_lobby',
    name: 'Clinic Lobby',
    promptFragments: { male: 'medical clinic lobby', female: 'medical clinic lobby' },
    compatibleUniforms: ['medical_scrubs', 'medical_coat']
  },
  {
    id: 'hospital_corridor',
    name: 'Hospital Corridor',
    promptFragments: { male: 'hospital corridor', female: 'hospital corridor' },
    compatibleUniforms: ['medical_scrubs', 'medical_coat']
  }
];


const ACADEMIC_BACKGROUND_PRESETS = [
  { id: 'university_library', name: 'University Library', promptFragments: { male: 'university library', female: 'university library' }, compatibleCategories: ['academic'], compatiblePresets: ['professor', 'graduation'] },
  { id: 'research_lab', name: 'Research Lab', promptFragments: { male: 'research laboratory', female: 'research laboratory' }, compatibleCategories: ['academic'], compatiblePresets: ['researcher'] },
  { id: 'campus_environment', name: 'Campus Environment', promptFragments: { male: 'campus environment', female: 'campus environment' }, compatibleCategories: ['academic'], compatiblePresets: ['student', 'graduation'] },
  { id: 'academic_office', name: 'Academic Office', promptFragments: { male: 'academic office', female: 'academic office' }, compatibleCategories: ['academic'], compatiblePresets: ['professor', 'researcher'] }
];


const ACADEMIC_PRESETS = [
  { id: 'professor', name: 'Professor', categoryId: 'academic', attire: { male: 'professional business attire with blazer or suit jacket', female: 'professional business attire with blazer or suit jacket' }, backgroundAndLighting: 'University library or office' },
  { id: 'researcher', name: 'Researcher', categoryId: 'academic', attire: { male: 'research lab attire', female: 'research lab attire' }, backgroundAndLighting: 'Research laboratory' },
  { id: 'student', name: 'Student', categoryId: 'academic', attire: { male: 'student attire', female: 'student attire' }, backgroundAndLighting: 'Campus environment' },
  {
    id: 'graduation',
    name: 'Graduation',
    categoryId: 'academic',
    attire: { male: 'graduation gown with cap', female: 'graduation gown with cap' },
    backgroundAndLighting: 'University campus or graduation ceremony setting',
    supportsColorOverride: true,
    defaultColor: '#000000' // Black
  }
];

/* FIX: Commented out unused const
const LOGO_POSITIONS = [
  { id: 'bottom_right', name: 'Bottom Right', promptFragment: 'bottom right corner' },
  { id: 'top_right', name: 'Top Right', promptFragment: 'top right corner' },
  { id: 'bottom_left', name: 'Bottom Left', promptFragment: 'bottom left corner' },
  { id: 'top_left', name: 'Top Left', promptFragment: 'top left corner' },
  { id: 'center_bottom', name: 'Center Bottom', promptFragment: 'centered at bottom' }
];
*/

const ASPECT_RATIO_OPTIONS = [
  // Portraits
  { id: '2:3', name: 'Classic Portrait (2:3)' },
  { id: '3:4', name: 'Portrait (3:4)' },
  { id: '4:5', name: 'Portrait (4:5)' },
  // Square
  { id: '1:1', name: 'Square (1:1)' },
  // Landscapes
  { id: '5:4', name: 'Landscape (5:4)' },
  { id: '4:3', name: 'Landscape (4:3)' },
  { id: '3:2', name: 'Classic Landscape (3:2)' },
];

/* FIX: Commented out unused const
const COLOR_TONE_OPTIONS = [
  { id: 'warm', name: 'Warm', description: 'Golden, warm tones' },
  { id:upload: 'Cool', description: 'Cool, blue-based tones' },
  { id: 'neutral', name: 'Neutral', description: 'Natural, balanced tones' },
  { id: 'vibrant', name: 'Vibrant', description: 'Bright, saturated colors' },
  { id: 'muted', name: 'Muted', description: 'Soft, desaturated tones' }
];
*/

const PRESET_OPTIONS = [
  ...ACADEMIC_PRESETS
];

const createBlankImage = (targetAspectRatioString) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const [targetWidth, targetHeight] = targetAspectRatioString.split(':').map(Number);
    const targetAspectRatio = targetWidth / targetHeight;

    canvas.width = 1024;
    canvas.height = 1024 / targetAspectRatio;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    resolve(canvas.toDataURL('image/png'));
  });
};

// Function to resize input image to 800px width to prevent overpowering blank templates
const resizeInputImage = (imageDataUrl, maxWidth = 800) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      console.log(`Original image dimensions: ${width}x${height}`);
      
      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height = Math.round(height * ratio); // Round to whole number to avoid decimals
        console.log(`Resizing image to: ${width}x${height}`);
      } else {
        console.log(`Image already at or below ${maxWidth}px width, no resizing needed`);
      }

      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);
      
      console.log(`Image resized successfully to ${width}x${height}`);
      resolve(canvas.toDataURL('image/jpeg', 0.9)); // Use JPEG for better compression
    };
    img.src = imageDataUrl;
  });
};

// NEW: Specific validation function for logos
const validateLogo = async (imagePart) => {
  try {
    const validationPrompt = `Analyze this image to determine if it is a technically suitable logo for placing on a photo. Focus ONLY on these technical aspects: 1. Is it a graphic, icon, or stylized text (not a photograph)? 2. Is it clear and not blurry? 3. Is the main subject clearly identifiable? Return ONLY valid JSON without markdown formatting: {"isValid": boolean, "message": string}. If valid, message should be "Logo looks good". If invalid, message should be brief like "Not a logo" or "Too blurry".`;

    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
    const response = await fetch(`${baseUrl}/api/generate-with-gemini`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: validationPrompt,
        isJson: true,
        userId: 'logo-validation',
        // Pass the image data
        images: [{
            base64: imagePart.base64,
            mimeType: imagePart.mimeType
        }]
      })
    });

    if (!response.ok) throw new Error('Validation failed');
    const result = await response.json();
    
    // The Gemini response is inside the 'generatedText' field
    const generatedText = result.generatedText;
    
    if (!generatedText) throw new Error('No generated text received.');
    
    // Clean up surrounding markdown (if any, though isJson=true should prevent it)
    const cleanText = generatedText.replace(/```json\s*|\s*```/g, '').trim();
    
    return JSON.parse(cleanText);
  } catch (error) {
    console.error('Validation error for logo:', error);
    return { isValid: true, message: 'Validation skipped' };
  }
};

// UPDATED: Simplified validation for non-logo images
const validateImage = async (imagePart, context) => {
  try {
    const validationPrompt = `Analyze this image for a ${context} headshot. Check if the subject is clearly visible and the image quality is sufficient for headshot generation. Return ONLY valid JSON without markdown formatting: {"isValid": boolean, "message": string}. If valid, message should be "Image looks good". If invalid, message should be brief like "Face not clear" or "Image too blurry".`;

    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
    const response = await fetch(`${baseUrl}/api/generate-with-gemini`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: validationPrompt,
        isJson: true,
        userId: 'image-validation',
        // Pass the image data
        images: [{
            base64: imagePart.base64,
            mimeType: imagePart.mimeType
        }]
      })
    });

    if (!response.ok) throw new Error('Validation failed');
    const result = await response.json();
    
    // The Gemini response is inside the 'generatedText' field
    const generatedText = result.generatedText;
    
    if (!generatedText) throw new Error('No generated text received.');
    
    // Clean up surrounding markdown (if any)
    const cleanText = generatedText.replace(/```json\s*|\s*```/g, '').trim();
    
    return JSON.parse(cleanText);
  } catch (error) {
    console.error('Validation error:', error);
    return { isValid: true, message: 'Validation skipped' };
  }
};

// REMOVED: validateBackgroundImage function

const detectGender = async (imagePart) => {
  try {
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
    const response = await fetch(`${baseUrl}/api/generate-with-gemini`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: 'Analyze this face image and determine the gender. Return only: male, female, or unknown',
        isJson: false,
        userId: 'gender-detection',
        // Pass the image data
        images: [{
            base64: imagePart.base64,
            mimeType: imagePart.mimeType
        }]
      })
    });

    if (!response.ok) throw new Error('Gender detection failed');
    const result = await response.json();
    return result.generatedText.trim().toLowerCase();
  } catch (error) {
    console.error('Gender detection error:', error);
    return 'unknown';
  }
};

// REMOVED: validateUniformImage function


// NEW: Two-step generation for multiple images
/* FIX: Commented out unused function
const generateHeadshotTwoStep = async (prompt, images, subjectImage, setDebugData) => {
  try {
    console.log('Starting two-step generation process...');

    // Step 1: Generate base image without subject face
    const step1Prompt = prompt.replace(/CRITICAL FACE PRESERVATION.*?\. /, '') +
      ' IMPORTANT: Generate a professional headshot with all elements EXCEPT the face. Create a placeholder for the face that will be added later.';

    console.log('Step 1 Prompt:', step1Prompt);
    console.log('Step 1 Images:', images.map((img, idx) => `Input ${idx + 1}: ${img.role || 'image'}`));
    const baseImage = await generateHeadshot(step1Prompt, images);
    console.log('Step 1 completed - base image generated');
    console.log('Step 1 Base Image Preview:', baseImage.substring(0, 100) + '...');

    // Step 2: Apply subject face to the base image using reference headshot approach
    //const step2Prompt = `A professional, high-resolution headshot photo. The subject should be well-groomed. The subject's face and identity are derived from the first provided image - Input 1: subject. Generate a new headshot where the subject is wearing the uniform and positioned in the environment shown in the second provided image - Input 2: base_image. The overall style, professional pose, background environment, and lighting MUST closely match the provided reference headshot image - Input 2: base_image. Seamlessly integrate the selfie subject into the style and composition of the reference image - Input 2: base_image. IMPORTANT: The final generated image must exactly match the aspect ratio of the very last image provided in the input sequence (the blank template image). The lighting on the subject must realistically match the background. The photo should be sharp, focused, and look like it was taken with a high-end DSLR camera.`;
    const step2Prompt = `Use the subject from Input 1: subject and place her into Input 2: base_image, maintaining the background, lighting, and attire of Image 2. The subject from Image 1 should be smiling and looking forward, consistent with the pose in Image 2.`;

    const step2Images = [
      { ...subjectImage, role: 'subject' },
      { base64: baseImage.split(',')[1], mimeType: 'image/png', role: 'base_image' }
    ];

    // Create debug images for Step 2
    const step2DebugImages = step2Images.map((img, index) => ({
      role: img.role,
      previewUrl: img.role === 'subject' ? `data:${img.mimeType};base64,${img.base64}` : baseImage
    }));

    console.log('Step 2 Prompt:', step2Prompt);
    console.log('Step 2 Images:', step2Images.map((img, idx) => `Input ${idx + 1}: ${img.role}`));

    // Update debug data to show Step 2 payload
    setDebugData({
      prompt: prompt,
      images: step2DebugImages,
      isTwoStep: true,
      step1Prompt: step1Prompt,
      step2Prompt: step2Prompt,
      step2Images: step2DebugImages
    });

    const finalImage = await generateHeadshot(step2Prompt, step2Images);
    console.log('Step 2 completed - final image generated');
    console.log('Step 2 Final Image Preview:', finalImage.substring(0, 100) + '...');

    return finalImage;
  } catch (error) {
    console.error('Two-step generation error:', error);
    throw new Error('Failed to generate headshot using two-step process. Please try again.');
  }
};
*/

const HeadshotGenerator = (props) => {
  // Destructure all props passed from App.js
  const {
    user, // FIX: Uncommented to show user email
    profile, onBack, /* onCreditDeduction, */ onSaveToDrive, onSessionExpired, // FIX: Added onSessionExpired
    generationAttempts, setGenerationAttempts,
    sessionGeneratedImages, setSessionGeneratedImages,
    imageExpirations, setImageExpirations, // <-- ADD THIS
    // TEAM-AWARE PROPS
    activeMode = 'personal',
    membership = null,
    teamCreditsAvailable = 0,
    teamTemplates = [],
    // EDITOR MODE PROPS
    mode = 'generate', // 'generate' or 'editor'
    db = null,
    storage = null,
    organization = null,
    onSaveTemplate = null,
    onExitEditor = null,

    // --- ADD THIS PROP ---
    showToast,
    // --- END ADD ---

    currentPreviewImage, setCurrentPreviewImage,
    uploadedImage, setUploadedImage,
    uploadedImagePreview, setUploadedImagePreview,
    selfieValidation, setSelfieValidation,
    // REMOVED: backgroundValidation props
    // REMOVED: corporateBackgroundValidation props
    // REMOVED: referenceHeadshotValidation props
    logoValidation, setLogoValidation,
    detectedGender, setDetectedGender,
    selectedCategoryId, setSelectedCategoryId,
    selectedPresetId, setSelectedPresetId,
    selectedAspectRatio, setSelectedAspectRatio,

    // Template editor state
    templateName,
    setTemplateName,
    templateDescription,
    setTemplateDescription,

    // Corporate state
    selectedCorporateWardrobeId, setSelectedCorporateWardrobeId,
    selectedCorporateBackgroundId, setSelectedCorporateBackgroundId,
    corporateBackgroundMode, setCorporateBackgroundMode,
    // REMOVED: uploadedCorporateBackground props
    // REMOVED: uploadedCorporateBackgroundPreview props
    // REMOVED: uploadedReferenceHeadshot props
    // REMOVED: uploadedReferenceHeadshotPreview props
    // REMOVED: corporateConsent props

    // Medical/Institutional state
    uniformMode, setUniformMode,
    backgroundMode, setBackgroundMode,
    selectedUniformPresetId, setSelectedUniformPresetId,
    selectedBackgroundPresetId, setSelectedBackgroundPresetId,

    // Academic state
    academicBackgroundMode, setAcademicBackgroundMode,
    backgroundColor, setBackgroundColor,
    // REMOVED: uploadedBackground props
    // REMOVED: uploadedBackgroundPreview props
    uploadedLogo, setUploadedLogo,
    uploadedLogoPreview, setUploadedLogoPreview,
    // colorToneOverride, setColorToneOverride, // FIX: Commented out unused prop
    uniformColorOverride, setUniformColorOverride,

    // Advanced settings
    blur, // setBlur, // FIX: Commented out unused prop
    brightness, // setBrightness, // FIX: Commented out unused prop
    tintColor, // setTintColor, // FIX: Commented out unused prop
    tintStrength, // setTintStrength, // FIX: Commented out unused prop
    vignette, // setVignette, // FIX: Commented out unused prop
    keepPerspective, // setKeepPerspective, // FIX: Commented out unused prop
    logoPosition, // setLogoPosition, // FIX: Commented out unused prop
    logoOpacity, /* setLogoOpacity, */ // FIX: Commented out unused prop
    // REMOVED: consent props
    logoConsent, setLogoConsent,
    logoPlacement, setLogoPlacement,
  } = props;

  // TEAM-AWARE CREDIT CHECK HELPER (useCallback for stable reference)
  const hasSufficientCredits = useCallback(() => {
    if (activeMode === 'team') {
      return teamCreditsAvailable >= 1;
    } else {
      return profile?.credits >= 1;
    }
  }, [activeMode, teamCreditsAvailable, profile?.credits]);

  const getAvailableCredits = useCallback(() => {
    if (activeMode === 'team') {
      return teamCreditsAvailable;
    } else {
      return profile?.credits || 0;
    }
  }, [activeMode, teamCreditsAvailable, profile?.credits]);

  // TEMPLATE ENFORCEMENT LOGIC
  const getAssignedTemplate = useCallback(() => {
    if (activeMode !== 'team' || !membership?.assignedTemplateId) {
      return null;
    }
    
    return teamTemplates.find(template => template.id === membership.assignedTemplateId);
  }, [activeMode, membership?.assignedTemplateId, teamTemplates]);

  // --- ADD THESE TWO LINES ---
  const assignedTemplate = useMemo(() => getAssignedTemplate(), [getAssignedTemplate]);
  const isTemplateRestricted = mode === 'generate' && assignedTemplate != null;
  // --- END ADD ---

  // Replace the old isOptionAllowed function with this one
  const isOptionAllowed = useCallback((optionId, optionType) => {
      if (!isTemplateRestricted) {
        return true; // Not in generate mode or no template, so everything is allowed
      }

      // Template is assigned. Enforce "Exact-Match".
      switch (optionType) {
        case 'category':
          return optionId === assignedTemplate.categoryId;
        
        // Corporate
        case 'wardrobe':
          return optionId === assignedTemplate.corporateWardrobeId;
        case 'corporate_background': // <-- This type will be updated in step 3
          return optionId === assignedTemplate.corporateBackgroundId;
        
        // Medical
        case 'medical_preset': // <-- This type will be updated in step 3
          return optionId === assignedTemplate.uniformPresetId;
        case 'medical_background': // <-- This type will be updated in step 3
          return optionId === assignedTemplate.backgroundPresetId;
        
        // Academic
        case 'academic_preset': // <-- This type will be updated in step 3
          return optionId === assignedTemplate.presetId;
        case 'academic_background': // <-- This type will be updated in step 3
          return optionId === assignedTemplate.academicBackgroundPresetId;
        
        default:
          return true; // Allow unknown types
      }
  }, [isTemplateRestricted, assignedTemplate]);

  const getTemplateRestrictionMessage = useCallback(() => {
    const assignedTemplate = getAssignedTemplate();
    if (!assignedTemplate) {
      return null;
    }
    
    return `Your team admin has restricted your options to the "${assignedTemplate.name}" template.`;
  }, [getAssignedTemplate]);

  // console.log('DEBUG: HeadshotGenerator component mounted with props:', {
  //   sessionGeneratedImages: sessionGeneratedImages.length,
  //   generationAttempts: generationAttempts
  // });

  // --- ONLY LOCAL, NON-PERSISTENT STATE REMAINS ---
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  // const [previousGeneratedImage, setPreviousGeneratedImage] = useState(null); // FIX: Commented out unused state
  // const [downloadedImageIds, setDownloadedImageIds] = useState(new Set()); // FIX: Commented out unused state

  // EDITOR MODE STATE (now passed as props from App.js)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setLocalError('Please enter a template name');
      return;
    }

    if (!db || !storage || !organization?.id) {
      console.error('Missing required dependencies:', { db: !!db, storage: !!storage, orgId: organization?.id });
      setLocalError('Cannot save template: missing required dependencies (Database or Storage connection)');
      return;
    }

    setIsSavingTemplate(true);
    setLocalError(null); // Clear previous errors

    try {
      // 1. Start with the base data common to all templates
      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        categoryId: selectedCategoryId,
        aspectRatio: selectedAspectRatio,
        backgroundColor: backgroundColor || '#e0e0e0',
        organizationId: organization.id,
        createdAt: new Date(),
        createdBy: profile?.userId || 'unknown',
      };

      // 2. Conditionally add settings based ONLY on the selected category
      switch (selectedCategoryId) {
        case 'corporate':
          templateData.corporateWardrobeId = selectedCorporateWardrobeId;
          templateData.corporateBackgroundMode = corporateBackgroundMode;
          templateData.presetId = null;
          if (corporateBackgroundMode === 'preset') {
            templateData.corporateBackgroundId = selectedCorporateBackgroundId;
          } else {
            templateData.corporateBackgroundId = null;
          }
          break;
        case 'medical':
          templateData.uniformMode = uniformMode;
          templateData.backgroundMode = backgroundMode;
          templateData.uniformPresetId = selectedUniformPresetId;
          templateData.backgroundPresetId = selectedBackgroundPresetId;
          templateData.uniformColorOverride = uniformColorOverride || '';
          templateData.logoPlacement = logoPlacement;
          templateData.logoOpacity = logoOpacity;
          break;
        case 'academic':
          templateData.presetId = selectedPresetId;
          templateData.academicBackgroundMode = academicBackgroundMode;
          templateData.academicBackgroundPresetId = selectedBackgroundPresetId;
          templateData.uniformColorOverride = uniformColorOverride || '';
          break;
        default:
          console.warn('Unknown category selected, saving base data only.');
      }

      // 3. Handle Logo Upload (The part that was getting stuck)
      let logoUrl = null;
      if (uploadedLogo) {
        console.log("Starting logo upload...");
        
        try {
            // Create a reference
            const logoPath = `team-templates/logos/${organization.id}/${Date.now()}_${uploadedLogo.name}`;
            const logoRef = ref(storage, logoPath);
            
            // Perform upload with metadata
            const metadata = { contentType: uploadedLogo.type };
            const logoSnapshot = await uploadBytes(logoRef, uploadedLogo, metadata);
            
            // Get URL
            logoUrl = await getDownloadURL(logoSnapshot.ref);
            templateData.logoUrl = logoUrl;
            console.log("Logo uploaded successfully:", logoUrl);
        } catch (uploadError) {
            console.error("Logo upload failed:", uploadError);
            throw new Error(`Failed to upload logo: ${uploadError.message}. Check your Storage Bucket config.`);
        }
      }

      // 4. Save to Firestore
      const templateRef = await addDoc(collection(db, 'teamTemplates'), templateData);
      
      console.log('Template saved successfully:', templateRef.id);
      
      // Reset form
      setTemplateName('');
      setTemplateDescription('');
      
      if (onSaveTemplate) {
        onSaveTemplate(templateRef.id);
      } else {
        if (showToast) {
            showToast('Template created successfully!', 'success');
        } else {
            setLocalError('Template created successfully!');
        }
        
        if (onExitEditor) {
            onExitEditor();
        }
      }
      
    } catch (error) {
      console.error('Error saving template:', error);
      // Show a more descriptive error to the user
      setLocalError(`Failed to save template: ${error.message}`);
    } finally {
      setIsSavingTemplate(false);
    }
};

  // MODIFIED: Simplified download handler
  /* FIX: Commented out unused function
  const handleDownload = (imageUrl, imageId) => {
    // Check if this image has already been paid for (by being in history)
    // or if the credit for this session was already spent.
    const isPaidFor = downloadedImageIds.has(imageId);
    if (isPaidFor) {
      return true; // Allow download without deducting credit
    }

    if (!hasSufficientCredits()) {
      setLocalError('You do not have enough credits to download this image.');
      return false; // Prevent download
    }

    // Call the centralized credit deduction function from App.js
    if (onCreditDeduction) {
      onCreditDeduction(1); // This will reset attempts and move images in App.js
    }

    // Mark as downloaded to allow re-downloads in the same session before navigating away
    setDownloadedImageIds(prev => new Set(prev).add(imageId));

    return true; // Proceed with download
  };
  */

  const [aspectRatioTemplatePreview, setAspectRatioTemplatePreview] = useState(null);

  // Validation states are now passed as props from App.js

  // NEW: State for the debug preview data
  // const [debugData, setDebugData] = useState({ prompt: null, images: [] }); // FIX: Commented out unused state

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result;
        const [meta, base64] = result.split(',');
        const mimeType = meta.split(':')[1].split(';')[0];
        resolve({ base64, mimeType });
      };
      reader.onerror = (error) => reject(error);
    });

  const dataURLToFile = useCallback((dataURL, filename) =>
    new Promise((resolve) => {
      const arr = dataURL.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const file = new File([u8arr], filename, { type: mime });
      resolve(file);
    }), []);

  const handleValidation = async (file, context) => {
    const setValidationState = {
      'selfie': setSelfieValidation,
      'logo': setLogoValidation
    }[context];

    // If context is not selfie or logo, do nothing
    if (!setValidationState) return;

    setValidationState({ status: 'validating', message: '' });
    try {
      const imagePart = await fileToBase64(file);
      let validationResult;

      if (context === 'logo') {
        validationResult = await validateLogo(imagePart);
      } else {
        // Default to selfie validation
        validationResult = await validateImage(imagePart, context);
      }

      setValidationState({ status: validationResult.isValid ? 'valid' : 'invalid', message: validationResult.message });
    } catch (err) {
      console.error(`Validation failed for ${context}:`, err);
      setValidationState({ status: 'invalid', message: 'Could not validate the image. Please try another.' });
    }
  };

  const handleImageUpload = async (file) => {
    try {
      console.log("Starting image upload WITH resizing...");
      
      // Convert File to data URL for resizing
      const fileDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      console.log("Created file data URL, starting resize...");
      
      const resizedImageDataUrl = await resizeInputImage(fileDataUrl);
      console.log("Image resize completed successfully");
      
      // Convert resized data URL back to File object for generation
      const resizedFile = await dataURLToFile(resizedImageDataUrl, file.name);
      console.log("Converted resized data URL back to File object");
      
      // Store the resized file for generation and resized preview for display
      setUploadedImage(resizedFile); // Store resized file for generation
      setUploadedImagePreview(resizedImageDataUrl); // Store resized data URL for preview
      setDetectedGender(null);
      handleValidation(resizedFile, 'selfie');

      const performGenderDetection = async () => {
        try {
          const imagePart = await fileToBase64(resizedFile);
          const gender = await detectGender(imagePart);
          setDetectedGender(gender);
        } catch (err) {
          console.error("Gender detection failed:", err);
          setDetectedGender('unknown');
        }
      };
      performGenderDetection();
    } catch (error) {
      console.error("Image upload failed:", error);
      setUploadedImage(file);
      setUploadedImagePreview(URL.createObjectURL(file));
      setDetectedGender(null);
      handleValidation(file, 'selfie');
    }
  };

  // REMOVED: handleBackgroundUpload
  const handleLogoUpload = (file) => {
    setUploadedLogo(file);
    setUploadedLogoPreview(URL.createObjectURL(file));
    handleValidation(file, 'logo');
  };
  // REMOVED: handleCorporateBackgroundUpload
  // REMOVED: handleReferenceHeadshotUpload
  
  // REMOVED: resetCorporateCustomization function

  useEffect(() => {
    const updateTemplatePreview = async () => {
      try {
        if(selectedAspectRatio) {
          const blankImage = await createBlankImage(selectedAspectRatio);
          setAspectRatioTemplatePreview(blankImage);
        }
      } catch (err) {
        console.error("Failed to create aspect ratio template preview:", err);
        setAspectRatioTemplatePreview(null);
      }
    };
    updateTemplatePreview();
  }, [selectedAspectRatio]);


  // DEBUG: Track sessionGeneratedImages state changes
  // useEffect(() => {
  //   console.log('DEBUG: sessionGeneratedImages state changed, count:', sessionGeneratedImages.length);
  // }, [sessionGeneratedImages]);


  // REMOVED: useEffect hook that cleared logo data

  // New handlers to replace the problematic useEffect
  const handleCorporateWardrobeSelect = (wardrobeId) => {
    setSelectedCorporateWardrobeId(wardrobeId);
    // If user selects a non-default wardrobe, reset custom uploads (REMOVED)
  };

  const handleCorporateBackgroundSelect = (backgroundId) => {
    setSelectedCorporateBackgroundId(backgroundId);
    // If user selects a non-default background, reset custom uploads (REMOVED)
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId);
    const presetsForCategory = PRESET_OPTIONS.filter(p => p.categoryId === categoryId);
    if (presetsForCategory.length > 0) {
      setSelectedPresetId(presetsForCategory[0].id);
    } else {
      setSelectedPresetId(null);
    }
    setSelectedCorporateWardrobeId(categoryId === 'corporate' ? 'business_formal' : null);
    setSelectedCorporateBackgroundId(categoryId === 'corporate' ? 'blurred_office' : null);
    // REMOVED: setUploadedBackground
    // REMOVED: setUploadedBackgroundPreview
    setUploadedLogo(null);
    setUploadedLogoPreview(null);
    // setPreviousGeneratedImage(null); // FIX: previousGeneratedImage is unused
    setLocalError(null);
    // REMOVED: setConsent
    setLogoConsent(false);
    // REMOVED: resetCorporateCustomization()
    // REMOVED: setBackgroundValidation
    setLogoValidation({ status: 'idle', message: '' });
  };

  const getBrightnessDescription = (value) => {
    if (value > 25) return "significantly brighter";
    if (value > 5) return "brighter";
    if (value < -25) return "significantly darker";
    if (value < -5) return "darker";
    return "normal";
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const constructPrompt = useCallback((settings, gender) => {
      let promptParts = [];

      promptParts.push("A professional, high-resolution corporate headshot photo.The subject is a well-groomed person with a clean hairstyle similar to the subject's hairstyle from the first provided image.");
      promptParts.push("The subject is the person from the first provided image.");

//      promptParts.push("A professional, high-resolution headshot photo. The subject's face and appearance should be enhanced but shouldn't change a lot from the first provide image to look polished, professional, and well-groomed with a clean hairstyle similar to the subjet's hair style from the first provided image.  The final image should look like a high-end studio portrait.");
//      promptParts.push("A professional, high-resolution headshot photo. The subject's face and appearance should be enhanced but shouldn't change a lot from the first provided image to make them look polished, professional, and well-groomed with a clean hairstyle similar to the subject's hairstyle from the first provided image. The final image should look like a high-end studio portrait taken by a professional photographer.");
//        promptParts.push("The subject's face and identity are derived from the first provided image.");
//      promptParts.push("A professional, high-resolution headshot photo. The subject's face and appearance should undergo subtle, natural retouching to enhance clarity and present a polished, professional, and well-groomed appearance. The enhancements should refine features without altering the subject's core identity or facial structure from the first provided image. Ensure a clean, tidy hairstyle that maintains the style from the first provided image.");
//      promptParts.push("A professional, high-resolution headshot photo. The final image should look like a high-end studio portrait taken by a professional photographer.");
//      promptParts.push("The subject's identity, based on the first provided image, must be clearly recognizable.");
//      promptParts.push("However, their face and overall appearance should undergo professional, natural-looking retouching to achieve a polished, impeccably groomed, and radiant look.");
//      promptParts.push("This includes: subtle skin smoothing, blemish reduction, evening of skin tone, gentle eye brightening and sharpening, and refinement of facial contours for enhanced clarity and definition.");
//      promptParts.push("Ensure a clean, perfectly styled, and tidy hairstyle that maintains the original style from the input image.");


      if (selectedCategoryId === 'corporate') {
        const { wardrobeId, backgroundId, backgroundMode } = settings.corporate;

        // REMOVED: Logic for 'upload_reference'
        
        const selectedWardrobe = CORPORATE_WARDROBE_OPTIONS.find(opt => opt.id === wardrobeId);
        if (selectedWardrobe) {
            promptParts.push(`They are wearing ${gender === 'female' ? selectedWardrobe.attire.female : selectedWardrobe.attire.male}.`);
        }
        if (backgroundMode === 'preset') {
            const selectedBackground = CORPORATE_BACKGROUND_OPTIONS.find(opt => opt.id === backgroundId);
            if (selectedBackground) promptParts.push(selectedBackground.backgroundAndLighting);
        } else if (backgroundMode === 'color') {
            promptParts.push(`The background is a solid, professional studio backdrop with the color ${settings.corporate.backgroundColor}.`);
        }
        // REMOVED: Logic for 'upload_background'
        
      } else if (selectedCategoryId === 'medical') {
        const { uniform, background, logo, advanced } = settings.institutional;

        // REMOVED: Check for reference mode
        
        // This is the normal logic for non-reference modes
        if (uniform.mode === 'preset') {
          const preset = MEDICAL_PRESETS.find(p => p.id === uniform.presetId);
          if (preset) {
            if (preset.id === 'medical_coat') {
              promptParts.push(`They are wearing ${gender === 'female' ? preset.promptFragments.female : preset.promptFragments.male}`);
            } else if (preset.id === 'medical_scrubs') {
              promptParts.push(`They are wearing clean, professional ${gender === 'female' ? preset.promptFragments.female : preset.promptFragments.male}`);
            } else {
              promptParts.push(`They are wearing ${gender === 'female' ? preset.promptFragments.female : preset.promptFragments.male}`);
            }
          }
        } else {
          // This case (uniform.mode === 'upload') might need to be removed if it's not supported without background upload
          // Keeping for now as uniform upload is separate from background upload
          promptParts.push('They are realistically wearing the uniform shown in the second provided image.');
        }
        if (uniform.uniformColorOverride) {
            promptParts.push(`The uniform should be ${uniform.uniformColorOverride} color.`);
        }

        if (background.mode === 'preset') {
          const preset = MEDICAL_BACKGROUND_PRESETS.find(p => p.id === background.presetId);
          promptParts.push(`The background is ${preset ? preset.promptFragments.male : 'a professional setting'}.`);
        } else if (background.mode === 'color') {
            promptParts.push(`The background is a solid, professional studio backdrop with the color ${background.color}.`);
        }
        // REMOVED: Logic for background.mode === 'upload'

        // Logo and Advanced controls apply to BOTH reference and normal modes
        if (uploadedLogo) {
          const logoContentInstruction = "The provided logo image (from the input sequence) MUST be applied EXACTLY AS-IS without ANY modifications. DO NOT resize, crop, stretch, or alter the logo in any way. PRESERVE ALL TEXT CONTENT COMPLETELY - do not chop, truncate, or modify any text. The entire logo including all text must be fully visible and readable. If the logo doesn't fit perfectly in the available space, make it smaller rather than truncating text. The logo should be applied as a direct overlay without distortion.";
          
          // MODIFIED: Removed 'background' placement option. 'uniform' is now the only option.
          const placementInstruction = `Apply the ${logoContentInstruction} onto the subject's uniform. The logo must realistically conform to the fabric. It should naturally wrap, bend, and distort with any folds, creases, and contours of the clothing. The lighting and shadows on the logo must match the lighting of the fabric beneath it, making it look like an authentic embroidered patch or a high-quality screen print, NOT a flat sticker applied after the fact.`;
          
          promptParts.push(`${placementInstruction} The logo must not obscure the subject's face and should have an opacity of approximately ${logo.opacity}%.`);
        }

        // Only apply advanced controls if NOT in reference mode (which is now always true)
        promptParts.push(`The background should have a professional blur (bokeh) with a strength of approximately ${advanced.blur} out of 20.`);
        promptParts.push(`The overall brightness should be ${getBrightnessDescription(advanced.brightness)}.`);
        if (advanced.tintStrength > 0) {
            promptParts.push(`The image should have a subtle ${advanced.tintColor} color tint applied with a strength of ${advanced.tintStrength}%.`);
        }
        if (advanced.vignette) {
            promptParts.push('Apply a subtle, dark vignette effect around the edges of the photo.');
        }

      } else {
        if (!settings.presetId) {
            throw new Error("Preset ID is missing.");
        }
        const selectedPreset = PRESET_OPTIONS.find(opt => opt.id === settings.presetId);
        if (selectedPreset) {
            const attire = gender === 'female' ? selectedPreset.attire.female : selectedPreset.attire.male;
            promptParts.push(`They are wearing ${attire}.`);

            // Add color override for graduation gown
            if (selectedPreset.id === 'graduation' && uniformColorOverride) {
              promptParts.push(`The graduation gown should be ${uniformColorOverride} color.`);
            }

            if (selectedCategoryId === 'academic') {
              const { backgroundMode, backgroundPresetId } = settings[selectedCategoryId];

              if (backgroundMode === 'preset') {
                const backgroundPresets = ACADEMIC_BACKGROUND_PRESETS;
                const selectedBackground = backgroundPresets.find(preset => preset.id === backgroundPresetId);
                if (selectedBackground) {
                  promptParts.push(`The background is ${selectedBackground.promptFragments.male}.`);
                } else {
                  promptParts.push(selectedPreset.backgroundAndLighting);
                }
              } else if (backgroundMode === 'color') {
                promptParts.push(`The background is a solid, professional studio backdrop with the color ${settings[selectedCategoryId].backgroundColor}.`);
              }
              // REMOVED: Logic for backgroundMode === 'upload'
            } else {
              promptParts.push(selectedPreset.backgroundAndLighting);
            }
        }
      }

      promptParts.push(`The final generated image must exactly match the aspect ratio of the very last image provided in the input sequence .`);
//      promptParts.push("IMPORTANT: The final generated image must exactly match the aspect ratio of the very last image provided in the input sequence (the blank template image).");
//      promptParts.push("The lighting on the subject must realistically match the background. The photo should be sharp, focused, and look like it was taken with a high-end DSLR camera. Auto white balanced. The lighting should be natural and not artificially enhanced. The photo should have a bright and clear subject, and the lighting should be diffused and natural. The subject should be in focus and the photo should have a crisp and clean look. The photo should not have any unwanted or distracting elements, such as shadows or reflections. The photo should be taken in a well-lit and well-lit environment, with no glare or excessive lighting.");
      promptParts.push("The lighting on the subject must realistically match the background.");
      promptParts.push("The photo should be sharp, focused, and look like it was taken with a high-end DSLR camera.");
//      promptParts.push("Auto white balanced. The lighting should be natural and not artificially enhanced.");
//      promptParts.push("The photo should have a bright and clear subject, and the lighting should be diffused and natural. The subject should be in focus and the photo should have a crisp and clean look. The photo should not have any unwanted or distracting elements, such as shadows or reflections. The photo should be taken in a well-lit environment, with no glare or excessive lighting.");
      return promptParts;
  },
    // --- ADD THIS LINE TO FIX THE ERROR ---
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // This is the fix. We must keep all state variables
      // that are read inside the prompt construction logic.
      selectedCategoryId, uploadedLogo, uniformColorOverride,
      backgroundColor, backgroundMode, blur, brightness,
      corporateBackgroundMode, keepPerspective, logoOpacity,
      logoPlacement, logoPosition, selectedBackgroundPresetId,
      selectedCorporateBackgroundId, selectedCorporateWardrobeId,
      selectedPresetId, selectedUniformPresetId, tintColor,
      tintStrength, uniformMode, vignette, academicBackgroundMode
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const performGeneration = useCallback(async (settings, isRegeneration = false) => {
    if (!uploadedImage) {
      setLocalError("Please upload a selfie to get started.");
      return null;
    }

    // Check if user has sufficient credits (team-aware)
    if (!hasSufficientCredits()) {
      setLocalError("You need at least 1 credit to generate headshots.");
      return null;
    }

    setIsLoading(true);
    setLocalError(null);
    if (!isRegeneration) {
        // setPreviousGeneratedImage(null); // FIX: previousGeneratedImage is unused
    }
    if(isRegeneration && currentPreviewImage){
      // setPreviousGeneratedImage(currentPreviewImage); // FIX: previousGeneratedImage is unused
    }

    try {
      // 1. Prepare images (already resized during upload)
      const imagesToUpload = [{ file: uploadedImage, role: 'subject' }];
      
      // Add logo file if present
      if (uploadedLogo) {
        imagesToUpload.push({ file: uploadedLogo, role: 'logo' });
      }

      // 2. Prepare image data as base64 for the API call
      const imagePartsForApi = await Promise.all(
        imagesToUpload.map(async (img) => {
          // console.log(`DEBUG: Preparing image for backend: ${img.role}`);
          const { base64, mimeType } = await fileToBase64(img.file);
          return { base64, mimeType, role: img.role }; // Keep role for potential future use
        })
      );

      // 3. Construct the final prompt (using your existing logic)
      let currentSettings;
      if (settings) {
          currentSettings = settings;
      } else {
           currentSettings = {
               presetId: selectedPresetId,
               corporate: {
                   wardrobeId: selectedCorporateWardrobeId,
                   backgroundId: selectedCorporateBackgroundId,
                   backgroundMode: corporateBackgroundMode,
                   backgroundColor: backgroundColor,
               },
               institutional: {
                   uniform: {
                       mode: uniformMode,
                       presetId: selectedUniformPresetId,
                       uniformColorOverride: uniformColorOverride,
                   },
                   background: {
                       mode: backgroundMode,
                       presetId: selectedBackgroundPresetId,
                       keepPerspective: keepPerspective,
                       color: backgroundColor,
                   },
                   logo: {
                       position: logoPosition,
                       opacity: logoOpacity,
                       placement: logoPlacement
                   },
                   advanced: {
                       blur: blur,
                       brightness: brightness,
                       tintColor: tintColor,
                       tintStrength: tintStrength,
                       vignette: vignette,
                   }
               },
               academic: {
                   backgroundMode: academicBackgroundMode,
                   backgroundPresetId: selectedBackgroundPresetId,
                   backgroundColor: backgroundColor,
               }
           };
      }

      const promptParts = constructPrompt(currentSettings, detectedGender);
      const finalPrompt = promptParts.join(' ');

      // 4. Call backend API endpoint
      // console.log(`DEBUG: Calling /api/generate-headshot for user: ${profile.userId}`);
      const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
      const response = await fetch(`${baseUrl}/api/generate-headshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: finalPrompt,
          images: imagePartsForApi, // Send the prepared base64 image data
          userId: profile.userId,   // Pass the Firebase User ID
          aspectRatio: selectedAspectRatio // Pass the selected aspect ratio
        })
      });

      // console.log(`DEBUG: Backend response status: ${response.status}`);

      // 5. Handle the response (expecting an image blob)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown server error' })); // Catch JSON parsing errors
        console.error('DEBUG: Backend generation error:', errorData);
        // Handle specific 429 Rate Limit error from backend
        if (response.status === 429) {
          setLocalError("The generation service is busy. Please wait a minute and try again.");
        } else {
          setLocalError(`Generation failed: ${errorData.error || `Server responded with ${response.status}`}`);
        }
        setIsLoading(false); // Stop loading on error
        return null; // Return null to indicate failure
      }

      // --- IMPORTANT: Process the JSON response ---
      const data = await response.json();
      const previewUrl = data.previewImage; // This is the data:image/jpeg;base64 string
      const imageId = data.imageId;

      // console.log(`DEBUG: Received watermarked preview and imageId: ${imageId}`);

      // 6. Update State (using functions passed from App.js)
      const imageObject = { url: previewUrl, id: imageId }; // <-- Create the object

      setCurrentPreviewImage(previewUrl); // This can still be just the URL
      setSessionGeneratedImages(prev => [...prev, imageObject]); // <-- Store the object
      setGenerationAttempts(prev => prev + 1); // Increment attempt count

      // --- ADD THIS BLOCK ---
      const expirationTime = Date.now() + 15 * 60 * 1000; // 15 minutes
      setImageExpirations(prev => ({
        ...prev,
        [imageId]: expirationTime
      }));
      // --- END ADD ---

      // console.log('DEBUG: State updated. Generation attempts:', generationAttempts + 1);

      return previewUrl; // Return the preview URL (for other logic)

    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "An unknown error occurred.";
      setLocalError(`Sorry, we couldn't generate your headshot. ${message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [
    uploadedImage, profile, currentPreviewImage, selectedPresetId,
    selectedCorporateWardrobeId, selectedCorporateBackgroundId, corporateBackgroundMode, backgroundColor,
    uniformMode, uniformColorOverride, backgroundMode, selectedUniformPresetId, selectedBackgroundPresetId, keepPerspective,
    logoPosition, logoOpacity, logoPlacement,
    blur, brightness, tintColor, tintStrength, vignette,
    // REMOVED: uploadedCorporateBackground, uploadedReferenceHeadshot,
    uploadedLogo, // REMOVED: uploadedBackground
    detectedGender, selectedAspectRatio, constructPrompt,
    academicBackgroundMode,
    setCurrentPreviewImage, setSessionGeneratedImages, setGenerationAttempts, setImageExpirations, setLocalError, setIsLoading, // FIX: Added missing dependencies
    hasSufficientCredits
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Image quality assessment function
  /* FIX: Commented out unused function
  const assessImageQuality = async (inputImage, generatedImage) => {
    // Basic quality checks that can be done client-side
    // In a real implementation, this would use a proper AI model for facial similarity

    try {
      // Check if images are valid
      if (!inputImage || !generatedImage) {
        return {
          isAcceptable: false,
          message: "Invalid images provided"
        };
      }

      // Basic image quality indicators
      // Note: This is a simplified implementation
      // In production, you would use proper computer vision APIs

      const issues = [];

      // Check image dimensions (basic quality indicator)
      const inputImg = new Image();
      const generatedImg = new Image();

      await new Promise((resolve) => {
        inputImg.onload = generatedImg.onload = resolve;
        inputImg.src = inputImage;
        generatedImg.src = generatedImage;
      });

      // Check if input image is too small
      if (inputImg.width < 300 || inputImg.height < 300) {
        issues.push("Input image resolution is too low");
      }

      // Check if generated image is valid
      if (generatedImg.width < 100 || generatedImg.height < 100) {
        issues.push("Generated image appears corrupted");
      }

      // Check for obvious quality issues
      // This is where you'd integrate with a proper facial similarity API
      // For now, we'll use a basic placeholder

      if (issues.length > 0) {
        return {
          isAcceptable: false,
          message: issues.join(", ")
        };
      }

      // If no obvious issues, consider it acceptable
      // In production, you'd want to implement proper facial similarity checking
      return {
        isAcceptable: true,
        message: "Image quality appears acceptable"
      };

    } catch (error) {
      console.error("Error assessing image quality:", error);
      return {
        isAcceptable: false,
        message: "Unable to assess image quality"
      };
    }
  };
  */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRegenerate = useCallback(() => {
    // Increment generation attempts
    performGeneration(null, true);
  }, [performGeneration]); // FIX: Removed unnecessary dependencies

  // Pre-generation quality check
  /* FIX: Commented out unused function
  const checkImageQualityBeforeGeneration = () => {
    if (!uploadedImage) {
      return { isValid: false, message: "Please upload a selfie first" };
    }

    // Use the existing validation system to check image quality
    if (selfieValidation.status === 'invalid') {
      return {
        isValid: false,
        message: `Image quality issue: ${selfieValidation.message}. Please upload a higher quality selfie.`
      };
    }

    // Additional quality checks
    const img = new Image();
    img.src = uploadedImage;

    const issues = [];

    // Check image dimensions
    if (img.width && img.height) {
      if (img.width < 300 || img.height < 300) {
        issues.push("Image resolution is too low (minimum 300x300 recommended)");
      }
      if (img.width > 4000 || img.height > 4000) {
        issues.push("Image resolution is too high (maximum 4000x4000)");
      }
    }

    if (issues.length > 0) {
      return {
        isValid: false,
        message: `Image quality issues detected: ${issues.join(', ')}. For best results, please upload a higher quality image.`
      };
    }

    return { isValid: true, message: "Image quality appears acceptable" };
  };
  */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isGenerateDisabled = useMemo(() => {
    if (isLoading || !uploadedImage) return true;
    // Disable button while validation is running or if validation failed
    if (selfieValidation.status === 'idle' || selfieValidation.status === 'invalid') return true;
    // Disable button if user doesn't have enough credits (team-aware)
    if (!hasSufficientCredits()) return true;
    // Disable button if retakes are exhausted
    if (generationAttempts >= 4) return true;

    if (selectedCategoryId === 'corporate') {
        if (!selectedCorporateWardrobeId) return true;
        if (corporateBackgroundMode === 'preset') {
            if (!selectedCorporateBackgroundId) return true;
        }
        // REMOVED: Checks for upload_background and upload_reference
        return false;
    }

    if (selectedCategoryId === 'medical') {
      // const isUploadingUniform = uniformMode === 'upload'; // FIX: Commented out unused var
      // REMOVED: isUploadingBg, isReferenceMode
      
      // REMOVED: check for isReferenceMode && !consent
      if (uploadedLogo && !logoConsent) return true;
      if (uniformMode === 'preset' && !selectedUniformPresetId) return true;
      // REMOVED: check for isUploadingBg
      // REMOVED: check for isReferenceMode
      if (backgroundMode === 'preset' && !selectedBackgroundPresetId) return true;
      if (uploadedLogo && logoValidation.status !== 'valid') return true;
      return false;
    }

    return !selectedPresetId;
  }, [
    isLoading, uploadedImage, selfieValidation.status,
    selectedCategoryId, selectedCorporateWardrobeId, selectedCorporateBackgroundId, corporateBackgroundMode,
    // REMOVED: corporateConsent, uploadedCorporateBackground, corporateBackgroundValidation.status, uploadedReferenceHeadshot, referenceHeadshotValidation.status,
    uniformMode, backgroundMode, logoConsent, selectedUniformPresetId, selectedBackgroundPresetId, uploadedLogo, logoValidation.status, // REMOVED: consent, uploadedBackground, backgroundValidation.status,
    selectedPresetId, generationAttempts, // FIX: Added missing dependency
    hasSufficientCredits // FIX: Added team-aware credit check dependency (handles profile dependency)
  ]); // FIX: Added disable comment

  const filteredPresets = useMemo(() => PRESET_OPTIONS.filter(preset => preset.categoryId === selectedCategoryId), [selectedCategoryId]);

  return (
    <div className="w-full min-h-screen bg-[#121212] text-white">
      <header className="flex items-center p-4 justify-between bg-[#121212] sticky top-0 z-10">
          <div className="flex items-center flex-1 pl-4 space-x-3">
            <img
              src="/InstantHeadshotPro logo.png"
              alt="InstantHeadshot.pro"
              className="h-10 w-auto bg-white p-1 rounded"
            />
            <h2 className="text-white text-lg font-bold leading-tight tracking-[-0.015em]">
              InstantHeadshot.pro
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            {/* Available Credits - Hidden on mobile, shown on larger screens (only in generate mode) */}
            {mode === 'generate' && (
              <div className="hidden md:block bg-[#1a1a1a] border border-[#FACC15] rounded-lg px-4 py-1">
                <span className="text-lg font-bold text-[#FACC15]">
                  Available Credits: {getAvailableCredits()}
                </span>
              </div>
            )}
            {/* Retakes Left - Hidden on mobile, shown on larger screens (only in generate mode) */}
            {mode === 'generate' && (
              <div className="hidden md:block bg-[#1a1a1a] border border-[#FACC15] rounded-lg px-4 py-1">
                <span className="text-lg font-bold text-[#FACC15]">
                  Retakes Left: {4 - generationAttempts}
                </span>
              </div>
            )}
            {mode === 'editor' && onExitEditor ? (
              <button onClick={onExitEditor} className="text-[#FACC15] text-base font-bold leading-normal tracking-[0.015em] shrink-0 hover:text-yellow-400 transition-colors">
                ← Exit Editor
              </button>
            ) : (
              <button onClick={onBack} className="text-[#FACC15] text-base font-bold leading-normal tracking-[0.015em] shrink-0 hover:text-yellow-400 transition-colors">
                ← Back
              </button>
            )}
          </div>
      </header>

      {/* Available Credits & Retakes Left - Shown below header on mobile only, sticky (only in generate mode) */}
      {mode === 'generate' && (
        <div className="md:hidden px-4 py-3 flex justify-center bg-[#121212] space-x-4 sticky top-16 z-10">
          <div className="bg-[#1a1a1a] border border-[#FACC15] rounded-lg px-4 py-2 flex-1 max-w-xs">
            <span className="text-base font-bold text-[#FACC15]">
              Credits: {getAvailableCredits()}
            </span>
          </div>
          <div className="bg-[#1a1a1a] border border-[#FACC15] rounded-lg px-4 py-2 flex-1 max-w-xs">
            <span className="text-base font-bold text-[#FACC15]">
              Retakes: {4 - generationAttempts}
            </span>
          </div>
        </div>
      )}

      {/* ALERTS ARE MOVED FROM ABOVE */}

      <main className="container mx-auto px-6 py-8">

        {/* --- NEW: User Greeting (Visible on all devices) --- */}
        <div className="mb-6 flex justify-between items-end border-b border-gray-800 pb-4">
            <div>
                <h1 className="text-2xl font-bold text-white">
                    {mode === 'editor' ? 'Template Editor' : 'Headshot Generator'}
                </h1>
                <p className="text-gray-400 text-sm mt-1">
                    Logged in as <span className="text-[#FACC15]">{user?.email}</span>
                </p>
            </div>
        </div>
        {/* --- END NEW --- */}

        {/* --- PASTE ALERTS HERE --- */}
        {!hasSufficientCredits() && (
          <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg relative mb-6" role="alert">
            You need at least 1 credit to generate headshots
          </div>
        )}
  
        {getTemplateRestrictionMessage() && (
          <div className="bg-yellow-900 border border-yellow-700 text-yellow-100 px-4 py-3 rounded-lg relative mb-6" role="alert">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{getTemplateRestrictionMessage()}</span>
            </div>
          </div>
        )}
        {/* --- END OF MOVED ALERTS --- */}
        <div className={`grid gap-8 ${mode === 'generate' ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
          {/* Controls Column */}
          <div className="flex flex-col gap-6">
            {/* Template Name & Description (Editor Mode Only) */}
            {mode === 'editor' && (
              <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">Template Details</h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="templateName" className="block text-sm font-medium text-gray-300 mb-1">
                      Template Name *
                    </label>
                    <input
                      id="templateName"
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="e.g., Medical Staff - Scrubs with Logo"
                      className="w-full px-4 py-3 bg-[#121212] border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FACC15]"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="templateDescription" className="block text-sm font-medium text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      id="templateDescription"
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e.target.value)}
                      placeholder="Describe this template for team members..."
                      rows="2"
                      className="w-full px-4 py-3 bg-[#121212] border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FACC15]"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Selfie Upload (Generate Mode Only) */}
            {mode === 'generate' && (
              <ImageUploader
                title="1. Your Selfie"
                onImageUpload={handleImageUpload}
                previewUrl={uploadedImagePreview}
                helpText="For best results: Front-facing photo, good lighting, clear focus, no extreme tilt. High-quality images produce better headshots."
                validationStatus={selfieValidation.status}
                validationMessage={selfieValidation.message}
                detectedGender={detectedGender}
              />
            )}

            <OptionSelector
              title="2. Choose a Category"
              options={CATEGORIES}
              selectedOptionId={selectedCategoryId}
              onSelectOption={handleCategorySelect}
              isOptionAllowed={isOptionAllowed}
              optionType="category"
            />

            {selectedCategoryId === 'corporate' && (
              <>
                <OptionSelector
                  title="3. Choose Wardrobe"
                  options={CORPORATE_WARDROBE_OPTIONS}
                  selectedOptionId={selectedCorporateWardrobeId}
                  onSelectOption={handleCorporateWardrobeSelect}
                  isOptionAllowed={isOptionAllowed}
                  optionType="wardrobe"
                />
                <CollapsibleSection title="4. Background Options">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <span className="font-semibold text-white">Mode:</span>
                    <label className="flex items-center cursor-pointer text-white">
                      <input type="radio" name="corporateBackgroundMode" value="preset" checked={corporateBackgroundMode === 'preset'} onChange={() => setCorporateBackgroundMode('preset')} className="mr-2" disabled={isTemplateRestricted} />
                      <span>Preset</span>
                    </label>
                    {/* REMOVED: Upload Radio Button */}
                    {/* REMOVED: Reference Headshot Radio Button */}
                    <label className="flex items-center cursor-pointer text-white">
                      <input type="radio" name="corporateBackgroundMode" value="color" checked={corporateBackgroundMode === 'color'} onChange={() => setCorporateBackgroundMode('color')} className="mr-2" disabled={isTemplateRestricted} />
                      <span>Solid Color</span>
                    </label>
                  </div>

                  {corporateBackgroundMode === 'preset' && (
                    <OptionSelector
                      title="Select Background Preset"
                      options={CORPORATE_BACKGROUND_OPTIONS}
                      selectedOptionId={selectedCorporateBackgroundId}
                      onSelectOption={handleCorporateBackgroundSelect}
                      isOptionAllowed={isOptionAllowed}
                      optionType="corporate_background" // <-- CHANGE THIS
                    />
                  )}

                  {/* REMOVED: corporateBackgroundMode === 'upload_background' block */}
                  {/* REMOVED: corporateBackgroundMode === 'upload_reference' block */}
                  {/* REMOVED: corporateConsent block */}

                  {corporateBackgroundMode === 'color' && (
                    <div className="flex items-center space-x-4">
                      <span className="font-semibold text-white">Background Color:</span>
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-12 h-8 border rounded"
                        disabled={isTemplateRestricted}
                      />
                      <span className="text-sm text-gray-300">{backgroundColor}</span>
                    </div>
                  )}
                </CollapsibleSection>
              </>
            )}

            {selectedCategoryId === 'medical' && (
              <>
                <CollapsibleSection title="3. Uniform Options">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="font-semibold text-white">Mode:</span>
                    <label className="flex items-center cursor-pointer text-white">
                      <input type="radio" name="uniformMode" value="preset" checked={uniformMode === 'preset'} onChange={() => setUniformMode('preset')} className="mr-2" disabled={isTemplateRestricted} />
                      <span>Select Preset</span>
                    </label>
                  </div>

                  {uniformMode === 'preset' && (
                    <>
                      <OptionSelector
                        title="Select Uniform Preset"
                        options={MEDICAL_PRESETS}
                        selectedOptionId={selectedUniformPresetId}
                        onSelectOption={setSelectedUniformPresetId}
                        isOptionAllowed={isOptionAllowed}
                        optionType="medical_preset" // <-- CHANGE THIS
                      />

                      {selectedUniformPresetId && MEDICAL_PRESETS.find(u => u.id === selectedUniformPresetId)?.supportsColorOverride && (
                        <div className="space-y-2 mt-4">
                          <label className="text-sm font-medium text-white">Color Tone Override (Optional)</label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={uniformColorOverride || MEDICAL_PRESETS.find(u => u.id === selectedUniformPresetId)?.defaultColor || '#2E8B57'}
                              onChange={(e) => setUniformColorOverride(e.target.value)}
                              className="w-10 h-10 rounded border border-gray-600 cursor-pointer"
                              disabled={isTemplateRestricted}
                            />
                            <input
                              type="text"
                              value={uniformColorOverride || MEDICAL_PRESETS.find(u => u.id === selectedUniformPresetId)?.defaultColor || '#2E8B57'}
                              onChange={(e) => setUniformColorOverride(e.target.value)}
                              placeholder="Hex color code"
                              className="flex-1 p-2 border border-gray-600 rounded text-sm bg-[#1a1a1a] text-white placeholder-gray-400"
                              disabled={isTemplateRestricted}
                            />
                          </div>
                          <p className="text-xs text-gray-400">Select a color to match your brand or preference</p>
                        </div>
                      )}
                    </>
                  )}

                </CollapsibleSection>

                <CollapsibleSection title="4. Background Options">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <span className="font-semibold text-white">Mode:</span>
                    <label className="flex items-center cursor-pointer text-white">
                      <input type="radio" name="backgroundMode" value="preset" checked={backgroundMode === 'preset'} onChange={() => setBackgroundMode('preset')} className="mr-2" disabled={isTemplateRestricted} />
                      <span>Preset</span>
                    </label>
                    {/* REMOVED: Upload Radio Button */}
                    {/* REMOVED: Reference Headshot Radio Button */}
                    <label className="flex items-center cursor-pointer text-white">
                      <input type="radio" name="backgroundMode" value="color" checked={backgroundMode === 'color'} onChange={() => setBackgroundMode('color')} className="mr-2" disabled={isTemplateRestricted} />
                      <span>Solid Color</span>
                    </label>
                  </div>

                  {backgroundMode === 'preset' && (
                    <OptionSelector
                      title="Select Background Preset"
                      options={MEDICAL_BACKGROUND_PRESETS.filter(bg =>
                        !selectedUniformPresetId ||
                        !bg.compatibleUniforms ||
                        bg.compatibleUniforms.includes(selectedUniformPresetId)
                      )}
                      selectedOptionId={selectedBackgroundPresetId}
                      onSelectOption={setSelectedBackgroundPresetId}
                      isOptionAllowed={isOptionAllowed}
                      optionType="medical_background" // <-- CHANGE THIS
                    />
                  )}

                  {/* REMOVED: backgroundMode === 'upload' block */}
                  {/* REMOVED: backgroundMode === 'upload_reference' block */}
                  
                  {backgroundMode === 'color' && (
                    <div className="flex items-center space-x-4">
                      <span className="font-semibold text-white">Background Color:</span>
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-12 h-8 border border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-300">{backgroundColor}</span>
                    </div>
                  )}
                </CollapsibleSection>

                {/* MODIFIED: Always show logo options now (was conditional) */}
                <CollapsibleSection title="5. Logo Options">
                  <ImageUploader
                    title="Upload Logo (Optional)"
                    onImageUpload={handleLogoUpload}
                    previewUrl={uploadedLogoPreview}
                    helpText="Upload your organization's logo. Higher resolution (e.g., >100KB) is recommended for best results."
                    validationStatus={logoValidation.status}
                    validationMessage={logoValidation.message}
                    footerText="Accepted: JPG, PNG, WebP. Max size: 20MB."
                  />

                  {uploadedLogo && (
                    <div className="my-4">
                      <h4 className="text-sm font-semibold text-white mb-2">Logo Placement:</h4>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center cursor-pointer text-white">
                          <input type="radio" name="logoPlacement" value="uniform" checked={logoPlacement === 'uniform'} onChange={() => setLogoPlacement('uniform')} className="mr-2" />
                          <span>On Uniform</span>
                        </label>
                        {/* REMOVED: In Background Radio Button */}
                      </div>
                    </div>
                  )}
                  {uploadedLogo && (
                    <div className="my-4">
                       {/* FIX: Slider component is commented out, so this is too.
                       <Slider
                          label="Logo Opacity"
                          value={logoOpacity}
                          min={10}
                          max={100}
                          onChange={setLogoOpacity}
                        />
                        */}
                    </div>
                  )}

                  {uploadedLogo && (
                    <div className="mt-4 p-3 bg-[#1a1a1a] rounded-lg border border-gray-700">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={logoConsent}
                          onChange={(e) => setLogoConsent(e.target.checked)}
                          className="mt-1 h-4 w-4 text-[#FACC15] border-gray-600 rounded focus:ring-[#FACC15]"
                        />
                        <div className="ml-3 text-sm">
                          <p className="text-white font-medium">I confirm I have the rights to use this logo.</p>
                          <p className="text-gray-400">You must have permission from the organization to use their logo.</p>
                        </div>
                      </label>
                    </div>
                  )}
                </CollapsibleSection>
              </>
            )}


            {selectedCategoryId === 'academic' && (
              <>
                <CollapsibleSection title="3. Academic Preset Selection">
                  <OptionSelector
                    title="Select Academic Preset"
                    options={filteredPresets}
                    selectedOptionId={selectedPresetId}
                    onSelectOption={(presetId) => {
                      setSelectedPresetId(presetId);
                      // Auto-select appropriate background preset
                      const selectedPreset = ACADEMIC_PRESETS.find(p => p.id === presetId);
                      if (selectedPreset) {
                        const compatibleBackground = ACADEMIC_BACKGROUND_PRESETS.find(bg =>
                          bg.compatiblePresets && bg.compatiblePresets.includes(presetId)
                        );
                        if (compatibleBackground) {
                          setSelectedBackgroundPresetId(compatibleBackground.id);
                        }
                      }
                    }}
                    isOptionAllowed={isOptionAllowed}
                    optionType="academic_preset" // <-- CHANGE THIS
                  />

                  {selectedPresetId === 'graduation' && (
                    <div className="space-y-2 mt-4">
                      <label className="text-sm font-medium">Gown Color Override (Optional)</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={uniformColorOverride || '#000000'}
                          onChange={(e) => setUniformColorOverride(e.target.value)}
                          className="w-10 h-10 rounded border cursor-pointer"
                          disabled={isTemplateRestricted}
                        />
                        <input
                          type="text"
                          value={uniformColorOverride || '#000000'}
                          onChange={(e) => setUniformColorOverride(e.target.value)}
                          placeholder="Hex color code"
                          className="flex-1 p-2 border rounded text-sm"
                          disabled={isTemplateRestricted}
                        />
                      </div>
                      <p className="text-xs text-gray-400">Select a color for the graduation gown</p>
                    </div>
                  )}
                </CollapsibleSection>

                <CollapsibleSection title="4. Background Options">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <span className="font-semibold text-white">Mode:</span>
                    <label className="flex items-center cursor-pointer text-white">
                      <input type="radio" name="academicBackgroundMode" value="preset" checked={academicBackgroundMode === 'preset'} onChange={() => setAcademicBackgroundMode('preset')} className="mr-2" disabled={isTemplateRestricted} />
                      <span>Preset</span>
                    </label>
                    {/* REMOVED: Upload Radio Button */}
                    {/* REMOVED: Reference Headshot Radio Button */}
                    <label className="flex items-center cursor-pointer text-white">
                      <input type="radio" name="academicBackgroundMode" value="color" checked={academicBackgroundMode === 'color'} onChange={() => setAcademicBackgroundMode('color')} className="mr-2" disabled={isTemplateRestricted} />
                      <span>Solid Color</span>
                    </label>
                  </div>

                  {academicBackgroundMode === 'preset' && (
                    <OptionSelector
                      title="Select Background Preset"
                      options={ACADEMIC_BACKGROUND_PRESETS.filter(bg =>
                        !selectedPresetId ||
                        !bg.compatiblePresets ||
                        bg.compatiblePresets.includes(selectedPresetId)
                      )}
                      selectedOptionId={selectedBackgroundPresetId}
                      onSelectOption={setSelectedBackgroundPresetId}
                      isOptionAllowed={isOptionAllowed}
                      optionType="academic_background" // <-- CHANGE THIS
                    />
                  )}

                  {/* REMOVED: academicBackgroundMode === 'upload' block */}
                  {/* REMOVED: academicBackgroundMode === 'upload_reference' block */}
                  
                  {academicBackgroundMode === 'color' && (
                    <div className="flex items-center space-x-4">
                      <span className="font-semibold text-white">Background Color:</span>
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-12 h-8 border border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-300">{backgroundColor}</span>
                    </div>
                  )}
                </CollapsibleSection>
              </>
            )}

            <div className="bg-[#1a1a1a] p-4 rounded-xl border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-2">Aspect Ratio</h3>
              <select value={selectedAspectRatio} onChange={(e) => setSelectedAspectRatio(e.target.value)} className="w-full p-2 border border-gray-600 rounded bg-[#1a1a1a] text-white" disabled={isTemplateRestricted}>
                {ASPECT_RATIO_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>

              {aspectRatioTemplatePreview && (
                <div className="bg-[#1a1a1a] p-4 rounded-xl border border-gray-700 mt-4">
                  {/* Side-by-side layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Aspect Ratio Preview with Specific Guidance */}
                    <div className="bg-gray-900 p-3 rounded-lg border border-gray-600 flex flex-col">
                      {/* Combined heading and guidance */}
                      <div className="text-center mb-3">
                        <h3 className="text-sm font-bold text-white mb-1">
                          Selected Aspect Ratio - <span className="text-[#FACC15]">{selectedAspectRatio}</span>
                        </h3>
                        {selectedAspectRatio === '1:1' && (
                          <div className="text-xs text-gray-300">
                            <p className="font-medium text-[#FACC15]">Perfect for Social Media</p>
                            <p className="text-gray-400 mt-1">LinkedIn, Instagram, Facebook, Twitter profile pictures</p>
                          </div>
                        )}
                        {selectedAspectRatio === '2:3' && (
                          <div className="text-xs text-gray-300">
                            <p className="font-medium text-[#FACC15]">Ideal for Portrait Formats</p>
                            <p className="text-gray-400 mt-1">Mobile apps, vertical banners, digital resumes</p>
                          </div>
                        )}
                        {selectedAspectRatio === '3:4' && (
                          <div className="text-xs text-gray-300">
                            <p className="font-medium text-[#FACC15]">Great for Mobile Apps</p>
                            <p className="text-gray-400 mt-1">Mobile applications, digital resumes, vertical content</p>
                          </div>
                        )}
                        {selectedAspectRatio === '4:5' && (
                          <div className="text-xs text-gray-300">
                            <p className="font-medium text-[#FACC15]">Perfect for Instagram</p>
                            <p className="text-gray-400 mt-1">Instagram posts, mobile apps, social media content</p>
                          </div>
                        )}
                        {selectedAspectRatio === '5:4' && (
                          <div className="text-xs text-gray-300">
                            <p className="font-medium text-[#FACC15]">Ideal for Print Materials</p>
                            <p className="text-gray-400 mt-1">Print materials, presentations, professional documents</p>
                          </div>
                        )}
                        {selectedAspectRatio === '4:3' && (
                          <div className="text-xs text-gray-300">
                            <p className="font-medium text-[#FACC15]">Great for Websites</p>
                            <p className="text-gray-400 mt-1">Website headers, presentations, print materials</p>
                          </div>
                        )}
                        {selectedAspectRatio === '3:2' && (
                          <div className="text-xs text-gray-300">
                            <p className="font-medium text-[#FACC15]">Perfect for Professional Use</p>
                            <p className="text-gray-400 mt-1">Professional portfolios, websites, business materials</p>
                          </div>
                        )}
                      </div>

                      {/* Preview section */}
                      <div className="text-center">
                        <p className="text-xs text-gray-300 mb-2">Your headshot will be generated in this format</p>
                        <img
                          src={aspectRatioTemplatePreview}
                          alt="Aspect ratio template preview"
                          className="mx-auto max-h-20 lg:max-h-32 rounded-lg border-2 border-[#FACC15] shadow-md"
                        />
                      </div>
                    </div>

                    {/* All Platform Guidance */}
                    <div className="bg-gray-900 p-3 rounded-lg border border-gray-600">
                      <h4 className="text-xs font-semibold text-[#FACC15] mb-2">All Aspect Ratios:</h4>
                      <div className="text-xs text-gray-300 space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="bg-[#FACC15] text-gray-900 text-xs font-bold px-2 py-1 rounded min-w-8 text-center">1:1</div>
                          <div className="flex-1">
                            <span className="font-medium">Social Media</span>
                            <p className="text-gray-400 text-xs mt-1">LinkedIn, Instagram, Facebook, Twitter</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="bg-[#FACC15] text-gray-900 text-xs font-bold px-2 py-1 rounded min-w-8 text-center">2:3</div>
                          <div className="flex-1">
                            <span className="font-medium">Portrait</span>
                            <p className="text-gray-400 text-xs mt-1">Mobile apps, vertical banners, resumes</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="bg-[#FACC15] text-gray-900 text-xs font-bold px-2 py-1 rounded min-w-8 text-center">3:4</div>
                          <div className="flex-1">
                            <span className="font-medium">Portrait</span>
                            <p className="text-gray-400 text-xs mt-1">Mobile apps, digital resumes</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="bg-[#FACC15] text-gray-900 text-xs font-bold px-2 py-1 rounded min-w-8 text-center">4:5</div>
                          <div className="flex-1">
                            <span className="font-medium">Portrait</span>
                            <p className="text-gray-400 text-xs mt-1">Instagram posts, mobile apps</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="bg-[#FACC15] text-gray-900 text-xs font-bold px-2 py-1 rounded min-w-8 text-center">5:4</div>
                          <div className="flex-1">
                            <span className="font-medium">Landscape</span>
                            <p className="text-gray-400 text-xs mt-1">Print materials, presentations</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="bg-[#FACC15] text-gray-900 text-xs font-bold px-2 py-1 rounded min-w-8 text-center">4:3</div>
                          <div className="flex-1">
                            <span className="font-medium">Landscape</span>
                            <p className="text-gray-400 text-xs mt-1">Websites, presentations, print</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="bg-[#FACC15] text-gray-900 text-xs font-bold px-2 py-1 rounded min-w-8 text-center">3:2</div>
                          <div className="flex-1">
                            <span className="font-medium">Landscape</span>
                            <p className="text-gray-400 text-xs mt-1">Professional portfolios, websites</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Generate Button (Generate Mode) or Save Template Button (Editor Mode) */}
            {mode === 'editor' ? (
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={isSavingTemplate || !templateName.trim()}
                className="w-full bg-gradient-to-r from-[#FACC15] to-yellow-400 hover:from-[#FACC15] hover:to-yellow-300 text-[#121212] font-bold py-3 px-4 rounded-lg text-lg transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSavingTemplate ? 'Saving Template...' : 'Save Team Template'}
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  performGeneration();
                }}
                disabled={isGenerateDisabled}
                className="w-full bg-gradient-to-r from-[#FACC15] to-yellow-400 hover:from-[#FACC15] hover:to-yellow-300 text-[#121212] font-bold py-3 px-4 rounded-lg text-lg transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? 'Generating...' : 'Generate Headshot'}
              </button>
            )}
          </div>

          {/* Preview Column (Generate Mode Only) */}
          {mode === 'generate' && (
            <div className="flex flex-col gap-6">
              <Preview
                isLoading={isLoading}
                error={localError || props.error}
                onRegenerate={handleRegenerate}
                onSaveToDrive={onSaveToDrive}
                onSessionExpired={onSessionExpired} // <-- ADD THIS
                profile={profile}
                generationAttempts={generationAttempts}
                sessionGeneratedImages={sessionGeneratedImages}
                imageExpirations={imageExpirations}
                hasSufficientCredits={hasSufficientCredits}
              />
            </div>
          )}
            {/* NEW: Render the debug preview component here */}
            {/* <DebugPreview prompt={debugData.prompt} images={debugData.images} /> */}

            {/* DEBUG: Show current state values */}
            {/* Debug state display commented out for production */}
            {/* <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="text-lg font-bold text-yellow-800 mb-2">DEBUG: Current State</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Generated Image URL:</div>
                <div className="font-mono text-xs truncate">{currentPreviewImage ? 'Present' : 'null'}</div>
                <div>Session Images Count:</div>
                <div>{sessionGeneratedImages.length}</div>
                <div>Generation Attempts:</div>
                <div>{generationAttempts}</div>
                <div>Is Loading:</div>
                <div>{isLoading ? 'true' : 'false'}</div>
                <div>Has Generated Image:</div>
                <div>{currentPreviewImage ? 'true' : 'false'}</div>
              </div>
            </div> */}
        </div>
      </main>
    </div>
  );
};

export default React.memo(HeadshotGenerator);